import React, { useState } from 'react';

/**
 * TwoFactorPanel — self-contained Security card for enabling/disabling
 * 2FA via TOTP (authenticator app) and Email OTP. Used inside the
 * dashboard's Security tab.
 *
 * Each factor maintains its own enrollment state machine so users can
 * enable/disable independently. We keep all state local — the only thing
 * we ever push back to the parent is `refreshUser()` after a successful
 * change so the surrounding UI reflects the new `user.twoFA.*` flags.
 */
export default function TwoFactorPanel({ user, authHeaders, refreshUser, showMsg }) {
  const totpOn = !!user?.twoFA?.totpEnabled;
  const emailOn = !!user?.twoFA?.emailEnabled;

  // ---- TOTP state ----
  const [totpStage, setTotpStage] = useState('idle'); // idle | enrolling | disabling
  const [totpQr, setTotpQr] = useState(null);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpDisablePw, setTotpDisablePw] = useState('');
  const [busy, setBusy] = useState(false);

  // ---- Email OTP state ----
  const [emailStage, setEmailStage] = useState('idle'); // idle | enrolling | disabling
  const [emailCode, setEmailCode] = useState('');
  const [emailDisablePw, setEmailDisablePw] = useState('');
  const [emailSentAt, setEmailSentAt] = useState(null);
  const [emailCooldown, setEmailCooldown] = useState(0);

  // Cooldown ticker for the resend button (15s).
  React.useEffect(() => {
    if (!emailCooldown) return;
    const t = setInterval(() => setEmailCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [emailCooldown]);

  const post = async (url, body) => {
    setBusy(true);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body || {})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      return data;
    } finally {
      setBusy(false);
    }
  };

  // ─── TOTP handlers ───
  const startTotp = async () => {
    try {
      const data = await post('/api/auth/2fa/totp/setup');
      setTotpQr(data.qr);
      setTotpSecret(data.secret);
      setTotpStage('enrolling');
      setTotpCode('');
    } catch (e) { showMsg('error', e.message); }
  };

  const confirmTotp = async () => {
    try {
      await post('/api/auth/2fa/totp/enable', { code: totpCode });
      showMsg('success', 'Đã bật xác thực 2 lớp qua app.');
      setTotpStage('idle'); setTotpQr(null); setTotpSecret(''); setTotpCode('');
      await refreshUser();
    } catch (e) { showMsg('error', e.message); }
  };

  const startTotpDisable = () => { setTotpStage('disabling'); setTotpCode(''); setTotpDisablePw(''); };

  const confirmTotpDisable = async () => {
    try {
      await post('/api/auth/2fa/totp/disable', { currentPassword: totpDisablePw, code: totpCode });
      showMsg('success', 'Đã tắt 2FA app.');
      setTotpStage('idle'); setTotpDisablePw(''); setTotpCode('');
      await refreshUser();
    } catch (e) { showMsg('error', e.message); }
  };

  // ─── Email OTP handlers ───
  const sendEmailCode = async () => {
    try {
      await post('/api/auth/2fa/email/send');
      setEmailSentAt(Date.now());
      setEmailCooldown(15);
      showMsg('success', `Đã gửi mã về ${user?.email}.`);
    } catch (e) { showMsg('error', e.message); }
  };

  const startEmail = async () => {
    setEmailStage('enrolling');
    setEmailCode('');
    await sendEmailCode();
  };

  const confirmEmail = async () => {
    try {
      await post('/api/auth/2fa/email/enable', { code: emailCode });
      showMsg('success', 'Đã bật xác thực 2 lớp qua email.');
      setEmailStage('idle'); setEmailCode(''); setEmailSentAt(null);
      await refreshUser();
    } catch (e) { showMsg('error', e.message); }
  };

  const startEmailDisable = async () => {
    setEmailStage('disabling');
    setEmailCode(''); setEmailDisablePw('');
    await sendEmailCode();
  };

  const confirmEmailDisable = async () => {
    try {
      await post('/api/auth/2fa/email/disable', { currentPassword: emailDisablePw, code: emailCode });
      showMsg('success', 'Đã tắt 2FA email.');
      setEmailStage('idle'); setEmailDisablePw(''); setEmailCode(''); setEmailSentAt(null);
      await refreshUser();
    } catch (e) { showMsg('error', e.message); }
  };

  return (
    <section className="dashboard-form" style={{ marginTop: 32 }}>
      <h3 className="dashboard-section-title">Bảo Vệ 2 Lớp (2FA)</h3>

      {/* ───────── TOTP (authenticator app) ───────── */}
      <div className="twofa-row">
        <div className="twofa-row-head">
          <div>
            <strong>Ứng dụng xác thực</strong>
            <div className="form-hint" style={{ marginTop: 4 }}>
              Trạng thái: <span className={totpOn ? 'twofa-on' : 'twofa-off'}>
                {totpOn ? 'Đang bật' : 'Đã tắt'}
              </span>
            </div>
          </div>
          {totpStage === 'idle' && (
            totpOn
              ? <button className="btn btn-outline btn-sm" onClick={startTotpDisable} disabled={busy}>Tắt</button>
              : <button className="btn btn-primary btn-sm" onClick={startTotp} disabled={busy}>Bật</button>
          )}
        </div>

        {totpStage === 'enrolling' && (
          <div className="twofa-body">
            <p>1. Mở app xác thực và quét mã QR dưới đây (hoặc nhập tay khoá: <code>{totpSecret}</code>).</p>
            {totpQr && <img src={totpQr} alt="TOTP QR" style={{ width: 180, height: 180, background: '#fff', padding: 8, borderRadius: 8 }} />}
            <p style={{ marginTop: 10 }}>2. Nhập mã 6 chữ số do app sinh ra để xác nhận:</p>
            <div className="twofa-actions">
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                style={{ width: 120, fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 4, textAlign: 'center' }}
              />
              <button className="btn btn-primary btn-sm" onClick={confirmTotp} disabled={busy || totpCode.length !== 6}>Xác nhận bật</button>
              <button className="btn btn-outline btn-sm" onClick={() => { setTotpStage('idle'); setTotpQr(null); setTotpSecret(''); setTotpCode(''); }}>Huỷ</button>
            </div>
          </div>
        )}

        {totpStage === 'disabling' && (
          <div className="twofa-body">
            <p>Nhập mật khẩu tài khoản và mã 6 chữ số hiện tại từ app xác thực để tắt 2FA.</p>
            <div className="twofa-actions" style={{ flexWrap: 'wrap' }}>
              <input type="password" placeholder="Mật khẩu" value={totpDisablePw} onChange={e => setTotpDisablePw(e.target.value)} />
              <input
                type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                style={{ width: 120, fontFamily: 'monospace', fontSize: '1.05rem', letterSpacing: 4, textAlign: 'center' }}
              />
              <button className="btn btn-danger btn-sm" onClick={confirmTotpDisable} disabled={busy || !totpDisablePw || totpCode.length !== 6}>Xác nhận tắt</button>
              <button className="btn btn-outline btn-sm" onClick={() => { setTotpStage('idle'); setTotpDisablePw(''); setTotpCode(''); }}>Huỷ</button>
            </div>
          </div>
        )}
      </div>

      {/* ───────── Email OTP ───────── */}
      <div className="twofa-row">
        <div className="twofa-row-head">
          <div>
            <strong>Xác thực qua Email</strong>
            <div className="form-hint" style={{ marginTop: 4 }}>
              Trạng thái: <span className={emailOn ? 'twofa-on' : 'twofa-off'}>
                {emailOn ? 'Đang bật' : 'Đã tắt'}
              </span>
            </div>
          </div>
          {emailStage === 'idle' && (
            emailOn
              ? <button className="btn btn-outline btn-sm" onClick={startEmailDisable} disabled={busy || !user?.email}>Tắt</button>
              : <button className="btn btn-primary btn-sm" onClick={startEmail} disabled={busy || !user?.email}>Bật</button>
          )}
        </div>

        {(emailStage === 'enrolling' || emailStage === 'disabling') && (
          <div className="twofa-body">
            <p>
              {emailStage === 'enrolling'
                ? 'Đã gửi mã 6 chữ số đến email của bạn. Mã có hiệu lực trong 5 phút.'
                : 'Mã xác nhận đã được gửi đến email. Nhập mật khẩu + mã để tắt.'}
            </p>
            <div className="twofa-actions" style={{ flexWrap: 'wrap' }}>
              {emailStage === 'disabling' && (
                <input type="password" placeholder="Mật khẩu" value={emailDisablePw} onChange={e => setEmailDisablePw(e.target.value)} />
              )}
              <input
                type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                value={emailCode}
                onChange={e => setEmailCode(e.target.value.replace(/\D/g, ''))}
                style={{ width: 120, fontFamily: 'monospace', fontSize: '1.05rem', letterSpacing: 4, textAlign: 'center' }}
              />
              {emailStage === 'enrolling' ? (
                <button className="btn btn-primary btn-sm" onClick={confirmEmail} disabled={busy || emailCode.length !== 6}>Xác nhận bật</button>
              ) : (
                <button className="btn btn-danger btn-sm" onClick={confirmEmailDisable} disabled={busy || !emailDisablePw || emailCode.length !== 6}>Xác nhận tắt</button>
              )}
              <button className="btn btn-outline btn-sm" onClick={sendEmailCode} disabled={busy || emailCooldown > 0}>
                {emailCooldown > 0 ? `Gửi lại (${emailCooldown}s)` : 'Gửi lại mã'}
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => { setEmailStage('idle'); setEmailCode(''); setEmailDisablePw(''); }}>Huỷ</button>
            </div>
            {emailSentAt && (
              <p className="form-hint" style={{ marginTop: 8 }}>
                Đã gửi lúc {new Date(emailSentAt).toLocaleTimeString('vi-VN')}.
                Nếu không thấy mail, kiểm tra Spam hoặc bấm "Gửi lại mã".
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
